/**
 * AgentFleetSettings
 * Deployment agent trust, autonomy tiers, and fleet management.
 * Rendered inside SettingsPage under /se/fleet.
 */

import { Shield, TrendingUp, TrendingDown, Minus, Award, AlertTriangle } from 'lucide-react';
import { useAgentTrust, useTrustHistory, type AutonomyTier, type TrustEvent } from '../../api/deployment';

const TIER_INFO: Record<AutonomyTier, { label: string; description: string; color: string; bgColor: string }> = {
  L1: {
    label: 'Level 1 \u2014 Approval Required',
    description: 'All deployments require human approval before proceeding.',
    color: 'text-muted',
    bgColor: 'bg-surface-subtle',
  },
  L2: {
    label: 'Level 2 \u2014 Semi-Autonomous',
    description: 'Staging deploys auto-approved. Production still requires human approval.',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50',
  },
  L3: {
    label: 'Level 3 \u2014 Autonomous',
    description: 'Auto-deploys staging and production. Approval only for migrations and infrastructure changes.',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
  },
};

function TrustGauge({ score, tier }: { score: number; tier: AutonomyTier }) {
  const tierInfo = TIER_INFO[tier];
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  const scoreColor = score >= 70 ? '#16a34a' : score >= 40 ? '#ca8a04' : '#dc2626';

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e5e5" strokeWidth="8" />
          <circle
            cx="60" cy="60" r="54" fill="none"
            stroke={scoreColor} strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-700"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-text">{score}</span>
          <span className="text-xs text-subtle">/ 100</span>
        </div>
      </div>
      <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${tierInfo.color} ${tierInfo.bgColor}`}>
        {tier}
      </div>
      <p className="text-xs text-muted text-center max-w-xs">{tierInfo.description}</p>
    </div>
  );
}

function PromotionRequirements({ tier, score, stats }: {
  tier: AutonomyTier;
  score: number;
  stats: { successfulDeployments: number; consecutiveSuccesses: number };
}) {
  if (tier === 'L3') {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600">
        <Award className="w-4 h-4" />
        Maximum autonomy achieved
      </div>
    );
  }

  const nextTier = tier === 'L1' ? 'L2' : 'L3';
  const reqs = nextTier === 'L2'
    ? { minScore: 70, minDeploys: 10, minConsecutive: 5 }
    : { minScore: 85, minDeploys: 20, minConsecutive: 10 };

  const items = [
    { label: 'Trust score', current: score, required: reqs.minScore, met: score >= reqs.minScore },
    { label: 'Successful deploys', current: stats.successfulDeployments, required: reqs.minDeploys, met: stats.successfulDeployments >= reqs.minDeploys },
    { label: 'Consecutive successes', current: stats.consecutiveSuccesses, required: reqs.minConsecutive, met: stats.consecutiveSuccesses >= reqs.minConsecutive },
  ];

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium text-muted uppercase tracking-wider">
        Requirements for {nextTier}
      </h3>
      {items.map(item => (
        <div key={item.label} className="flex items-center justify-between text-sm">
          <span className="text-muted">{item.label}</span>
          <span className={item.met ? 'text-green-600' : 'text-subtle'}>
            {item.current} / {item.required}
            {item.met && ' \u2713'}
          </span>
        </div>
      ))}
    </div>
  );
}

function TrustTimeline({ events }: { events: TrustEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-subtle py-8 text-center">No trust events yet.</p>;
  }

  return (
    <div className="space-y-1">
      {events.map(event => {
        const isPositive = event.delta > 0;
        const isNegative = event.delta < 0;
        const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
        const deltaColor = isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-subtle';

        return (
          <div key={event.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-surface-muted">
            <Icon className={`w-4 h-4 shrink-0 ${deltaColor}`} />
            <span className={`text-sm font-mono w-12 text-right ${deltaColor}`}>
              {isPositive ? '+' : ''}{event.delta}
            </span>
            <span className="text-sm text-text flex-1 truncate">
              {event.reason || event.eventType.replace(/_/g, ' ')}
            </span>
            <span className="text-xs text-subtle font-mono">{event.newTier}</span>
            <span className="text-xs text-subtle font-mono w-8 text-right">{event.newScore}</span>
            <span className="text-xs text-subtle shrink-0">
              {new Date(event.createdAt).toLocaleDateString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TierBehaviorTable() {
  return (
    <div className="rounded-lg border border-line overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-subtle text-muted">
            <th className="text-left px-4 py-2">Tier</th>
            <th className="text-left px-4 py-2">Staging</th>
            <th className="text-left px-4 py-2">Production</th>
          </tr>
        </thead>
        <tbody className="text-text">
          <tr className="border-t border-line">
            <td className="px-4 py-2 font-medium text-muted">L1</td>
            <td className="px-4 py-2"><span className="text-amber-600">Approval required</span></td>
            <td className="px-4 py-2"><span className="text-amber-600">Approval required</span></td>
          </tr>
          <tr className="border-t border-line">
            <td className="px-4 py-2 font-medium text-cyan-700">L2</td>
            <td className="px-4 py-2"><span className="text-green-600">Auto-deploy</span></td>
            <td className="px-4 py-2"><span className="text-amber-600">Approval required</span></td>
          </tr>
          <tr className="border-t border-line">
            <td className="px-4 py-2 font-medium text-green-700">L3</td>
            <td className="px-4 py-2"><span className="text-green-600">Auto-deploy</span></td>
            <td className="px-4 py-2"><span className="text-green-600">Auto-deploy*</span></td>
          </tr>
        </tbody>
      </table>
      <p className="px-4 py-2 text-xs text-subtle border-t border-line">
        * L3 production auto-deploy excludes: DB migrations, compose changes, infrastructure tier services
      </p>
    </div>
  );
}

export function AgentFleetSettings() {
  const { data: trustData, isLoading: trustLoading } = useAgentTrust();
  const { data: historyData } = useTrustHistory();

  const trust = trustData?.data;
  const events = historyData?.data || [];

  if (trustLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="flex items-center gap-3 text-subtle animate-pulse">
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {trust ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trust gauge */}
          <div className="p-6 rounded-lg border border-line bg-white flex flex-col items-center">
            <TrustGauge score={trust.trustScore} tier={trust.autonomyTier} />
          </div>

          {/* Stats */}
          <div className="p-6 rounded-lg border border-line bg-white space-y-4">
            <h2 className="text-sm font-medium text-muted uppercase tracking-wider">Statistics</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-2xl font-bold text-text">{trust.totalDeployments}</p>
                <p className="text-xs text-muted">Total Deploys</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{trust.successfulDeployments}</p>
                <p className="text-xs text-muted">Successful</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{trust.failedDeployments}</p>
                <p className="text-xs text-muted">Failed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-cyan-600">{trust.consecutiveSuccesses}</p>
                <p className="text-xs text-muted">Streak</p>
              </div>
            </div>
            {trust.lastDeploymentAt && (
              <p className="text-xs text-subtle">
                Last deploy: {new Date(trust.lastDeploymentAt).toLocaleString()}
              </p>
            )}
          </div>

          {/* Promotion requirements */}
          <div className="p-6 rounded-lg border border-line bg-white">
            <PromotionRequirements
              tier={trust.autonomyTier}
              score={trust.trustScore}
              stats={{
                successfulDeployments: trust.successfulDeployments,
                consecutiveSuccesses: trust.consecutiveSuccesses,
              }}
            />
          </div>
        </div>
      ) : (
        <div className="p-6 rounded-lg border border-line bg-white text-center text-muted">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-subtle" />
          <p>No deployment agent configured yet.</p>
          <p className="text-xs mt-1">The agent will be initialized after the first deployment.</p>
        </div>
      )}

      {/* Tier behavior */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-muted uppercase tracking-wider">Tier Behavior</h2>
        <TierBehaviorTable />
      </div>

      {/* Trust history */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-muted uppercase tracking-wider">Trust History</h2>
        <div className="rounded-lg border border-line overflow-hidden bg-white">
          <TrustTimeline events={events} />
        </div>
      </div>
    </div>
  );
}
